import React, { useState, useMemo, useEffect, useId } from 'react';
import { App } from 'antd';
import { importStudentsExcel } from '../../api/studentImport';
import { listStudents } from '../../api/students';
import TableCard from '../../components/TableCard';
import './StudentImport.css';

export default function StudentImport() {
  const { notification } = App.useApp();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dbStudents, setDbStudents] = useState([]);
  const fileInputId = useId();

  const fetchDbStudents = async () => {
    try {
      const data = await listStudents({});
      setDbStudents(data || []);
    } catch (e) {
      console.error('Failed to fetch students', e);
    }
  };

  useEffect(() => {
    fetchDbStudents();
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    
    // Check extension
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      notification.error({ message: 'Seuls les fichiers .xlsx sont acceptés.' });
      e.target.value = '';
      return;
    }

    // Check size (2MB max)
    if (f.size > 2 * 1024 * 1024) {
      notification.error({ message: 'Le fichier ne doit pas dépasser 2 Mo.' });
      e.target.value = '';
      return;
    }

    setFile(f);
    setResult(null);
    setSearchTerm('');
    setStatusFilter('ALL');
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setSearchTerm('');
    setStatusFilter('ALL');
    try {
      const data = await importStudentsExcel(file);
      setResult(data);
      if (data.inserted > 0) {
        notification.success({ message: `${data.inserted} étudiant(s) whitelisté(s) avec succès.` });
      } else {
        notification.info({ message: 'Aucun nouvel étudiant ajouté (doublons ou erreurs).' });
      }
      await fetchDbStudents(); // refresh background list
    } catch (e) {
      notification.error({ message: 'Erreur lors de l\'import.', description: String(e?.message || e) });
    } finally {
      setLoading(false);
    }
  };

  const currentDetails = useMemo(() => {
    return dbStudents.map((s) => ({
      student_code: s.student_code,
      nom: s.User?.nom,
      prenom: s.User?.prenom,
      email: s.User?.email,
      age: s.age,
      gender: s.gender,
      status: 'saved',
      reason: s.RoomAssignments?.length > 0 ? `Chambre: ${s.RoomAssignments[0].Room.code}` : 'Whitelisté (attente chambre)',
    }));
  }, [dbStudents]);

  const filteredDetails = useMemo(() => {
    return currentDetails.filter(d => {
      if (statusFilter !== 'ALL' && d.status !== statusFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const str = `${d.email} ${d.nom || ''} ${d.prenom || ''} ${d.student_code || ''}`.toLowerCase();
        if (!str.includes(term)) return false;
      }
      return true;
    });
  }, [currentDetails, searchTerm, statusFilter]);

  const fileName = file ? file.name : 'Aucun fichier sélectionné';

  const renderStatusTag = (status) => {
    if (status === 'saved') return <span className="tag tag-blue">En base</span>;
    if (status === 'inserted') return <span className="tag tag-green">Ajouté</span>;
    if (status === 'skipped') return <span className="tag tag-orange">Déjà whitelisté</span>;
    if (status === 'error') return <span className="tag tag-red">Erreur</span>;
    return <span className="tag tag-blue">En base</span>;
  };

  return (
    <div className="whitelistRoot">
      <main className="page">
        <div className="section-label">Whitelist Étudiants — Import &amp; Liste complète</div>

        <div className="card">
          <div className="card-inner">
            <p className="info-text">
              Sélectionnez un fichier Excel (<strong>.xlsx</strong>) pour importer des étudiants. Le fichier devrait idéalement contenir ces colonnes (insensibles à la casse) :
              <code>email</code>, <code>nom</code>, <code>prenom</code>, <code>age</code>, <code>sexe/genre</code>, <code>ID/matricule/student_code</code>. La liste ci-dessous affiche <strong>tous les étudiants whitelistés</strong> (même sans chambre).
            </p>

            <div className="import-row">
              <label className="file-input-label" htmlFor={fileInputId}>
                <div className={`file-input-wrap${file ? ' file-input-wrap--active' : ''}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" x2="12" y1="3" y2="15" />
                  </svg>
                  <span className={file ? 'file-name--active' : ''}>{fileName}</span>
                </div>
              </label>

              <input id={fileInputId} type="file" accept=".xlsx" onChange={handleFileChange} disabled={loading} />

              <button className="btn-primary" onClick={handleUpload} disabled={!file || loading}>
                {loading ? 'Importation...' : 'Uploader & Importer'}
              </button>

              {result && (
                <button className="btn-secondary" onClick={() => setResult(null)} disabled={loading}>
                  Fermer le rapport
                </button>
              )}
            </div>

            {result && (
              <div className="import-report">
                <div className="import-report-inner">
                  <div className="import-report-title">Résultat de l'import</div>
                  <div className="import-report-grid">
                    <div className="import-report-kpi">
                      <div className="import-report-kpi-value">{result.inserted}</div>
                      <div className="import-report-kpi-label">Insérés</div>
                    </div>
                    <div className="import-report-kpi">
                      <div className="import-report-kpi-value">{result.skipped}</div>
                      <div className="import-report-kpi-label">Ignorés</div>
                    </div>
                    {result.errors && result.errors.length > 0 && (
                      <div className="import-report-kpi">
                        <div className="import-report-kpi-value">{result.errors.length}</div>
                        <div className="import-report-kpi-label">Erreurs</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="list-header">
              <span className="list-title">Tous les étudiants whitelistés ({filteredDetails.length}/{currentDetails.length})</span>
              <div className="list-controls">
                <input
                  className="search-input"
                  type="text"
                  placeholder="Rechercher (Nom, Email, ID...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div className="select-wrap">
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="ALL">Tous les statuts</option>
                    <option value="saved">En base de données</option>
                  </select>
                  <svg viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </div>

            <TableCard rows={filteredDetails} pageSize={20}>
              {(pageRows) => (
                <table>
                  <thead>
                    <tr>
                      <th>ID Étudiant</th>
                      <th>Nom &amp; Prénom</th>
                      <th>Email</th>
                      <th>Âge</th>
                      <th>Genre</th>
                      <th>Statut</th>
                      <th>Détail / Chambre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((d, idx) => (
                      <tr key={`${d.email}-${idx}`}>
                        <td className="td-strong">{d.student_code || '—'}</td>
                        <td className="td-strong">{d.nom || d.prenom ? `${d.prenom || ''} ${d.nom || ''}`.trim() : '—'}</td>
                        <td className="td-muted">{d.email || '—'}</td>
                        <td className="td-muted">{d.age ?? '—'}</td>
                        <td className="td-muted">{d.gender || '—'}</td>
                        <td>{renderStatusTag(d.status)}</td>
                        <td className="td-muted td-ellipsis" title={d.reason || ''}>
                          {d.reason || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </TableCard>
          </div>
        </div>
      </main>
    </div>
  );
}
